/**
 * Local weighbridge agent on the weighbridge PC:
 *   GET http://127.0.0.1:5000/get-weight
 *   GET http://127.0.0.1:5000/snapshot1
 *   GET http://127.0.0.1:5000/snapshot2
 *   GET http://127.0.0.1:5000/video_feed1
 *   GET http://127.0.0.1:5000/video_feed2
 */

import { nowIndiaIso } from "./indiaTime";
import { api, API_BASE, extractError } from "./api";

const DEFAULT_AGENT_BASE = "http://127.0.0.1:5000";

export function getLocalScaleAgentBaseUrl() {
  const fromEnv = process.env.REACT_APP_LOCAL_SCALE_URL || "";
  if (fromEnv) {
    return fromEnv.replace(/\/get-weight\/?$/i, "").replace(/\/$/, "") || DEFAULT_AGENT_BASE;
  }
  return DEFAULT_AGENT_BASE;
}

export function getLocalScaleAgentUrl() {
  return `${getLocalScaleAgentBaseUrl()}/get-weight`;
}

export function getLocalVideoFeedUrls() {
  const base = getLocalScaleAgentBaseUrl();
  const bust = Date.now();
  return {
    feed1: `${base}/video_feed1?_=${bust}`,
    feed2: `${base}/video_feed2?_=${bust}`,
  };
}

async function fetchAgentJson(path) {
  const url = `${getLocalScaleAgentBaseUrl()}${path}`;
  let response;
  try {
    response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
  } catch {
    throw new Error(
      "Cannot reach the local scale agent at localhost:5000. Start local-scale-agent on this PC (python app.py).",
    );
  }
  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error("Scale agent returned an invalid response.");
  }
  return { response, data };
}

async function fetchAgentJpeg(path, label) {
  const url = `${getLocalScaleAgentBaseUrl()}${path}`;
  let response;
  try {
    response = await fetch(url, { method: "GET", cache: "no-store" });
  } catch {
    throw new Error(`Cannot reach local agent for ${label}.`);
  }
  if (!response.ok) {
    throw new Error(`${label} snapshot failed (${response.status}).`);
  }
  const blob = await response.blob();
  if (!blob || blob.size < 100) {
    throw new Error(`${label} snapshot is empty — check camera stream.`);
  }
  return blob;
}

/**
 * @returns {Promise<number>} weight in kg
 */
export async function fetchLocalScaleWeightKg() {
  const { response, data } = await fetchAgentJson("/get-weight");
  if (!response.ok || data?.status !== "success") {
    throw new Error(data?.message || `Scale read failed (${response.status}).`);
  }
  const weight = Number(data.weight);
  if (!Number.isFinite(weight) || weight < 0) {
    throw new Error("Scale returned an invalid weight.");
  }
  return weight;
}

function packCapture(data, source) {
  const weightKg = Number(data?.weight_kg ?? data?.weight);
  return {
    weightKg,
    weighedAt: data?.weighed_at || nowIndiaIso(),
    camera1SnapshotUrl: data?.camera1_snapshot_url || data?.camera1_snapshot_uri || null,
    camera2SnapshotUrl: data?.camera2_snapshot_url || data?.camera2_snapshot_uri || null,
    camera1ViewUrl: data?.camera1_snapshot_url || data?.camera1_snapshot_uri || null,
    camera2ViewUrl: data?.camera2_snapshot_url || data?.camera2_snapshot_uri || null,
    photosCaptured: Boolean(data?.photos_captured),
    photoWarning: data?.photo_warning || null,
    source,
  };
}

/**
 * Lock kg + high-res camera stills from the mill edge agent (ERP `/weighbridge/capture`).
 * Never archives live-preview JPEGs.
 */
export async function fetchScaleWeightAndCameraSnapshots({
  entityType = "purchase",
  entityCode = "",
  gatePassCode = "",
  kind = "gross",
  allowUnstable = false,
} = {}) {
  try {
    const { data } = await api.post("/weighbridge/capture", {
      entity_type: entityType,
      entity_code: entityCode || "txn",
      gate_pass_code: gatePassCode || entityCode || "txn",
      kind,
      allow_unstable: Boolean(allowUnstable),
    });
    const weightKg = Number(data?.weight_kg ?? data?.weight);
    if (!Number.isFinite(weightKg) || weightKg < 0) {
      throw new Error("Cloud scale returned an invalid weight.");
    }
    return packCapture(data, "cloud-edge");
  } catch (err) {
    if (String(err?.message || "").startsWith("Cloud scale")) throw err;
    const status = err?.response?.status;
    if (status === 409 || status === 403) {
      throw new Error(extractError(err) || err?.message || "Scale is not stable");
    }
    throw new Error(
      extractError(err) ||
        "Could not lock weight from the mill edge agent. Confirm start-edge-agent.bat is connected to this ERP.",
    );
  }
}

/** Browser <img> src for a stored weigh photo (s3://, media://, or HTTPS). */
export function weighPhotoDisplaySrc(uriOrUrl) {
  const raw = String(uriOrUrl || "").trim();
  if (!raw) return "";
  const token =
    typeof localStorage !== "undefined" ? localStorage.getItem("bansal_token") || "" : "";
  const withToken = (url) => {
    if (!token) return url;
    return `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
  };
  if (raw.startsWith("s3://") || raw.startsWith("media://")) {
    return withToken(`${API_BASE}/weighbridge/photo?uri=${encodeURIComponent(raw)}`);
  }
  if (raw.startsWith("/api/weighbridge/media/") || raw.startsWith("/weighbridge/media/")) {
    const path = raw.startsWith("/api/") ? raw.slice("/api".length) : raw;
    return withToken(`${API_BASE}${path}`);
  }
  return raw;
}

/** Dispatch native input/change so non-React listeners also see the update. */
export function applyWeightToInputElement(inputEl, weightKg) {
  if (!inputEl) return;
  const value = String(weightKg);
  const proto = window.HTMLInputElement.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(proto, "value");
  if (descriptor?.set) {
    descriptor.set.call(inputEl, value);
  } else {
    inputEl.value = value;
  }
  inputEl.dispatchEvent(new Event("input", { bubbles: true }));
  inputEl.dispatchEvent(new Event("change", { bubbles: true }));
}
